import React, {forwardRef, useEffect, useReducer, useState, useRef} from 'react';
import {defaultSubtitleStyle, Subtitle, SubtitleStyle} from '@/models/Subtitle';
import {lineBoundsBox} from '@/models/SubtitleStyle';
import {loadFont, getLineHeightRatio, getAssToCssScaleFactor} from '@/services/googleFontsService';

const fontMetricsCache = new Map<string, { lineHeightRatio: number; scaleFactor: number }>();

interface SubtitleOverlayProps {
    subtitle: Subtitle | null;
    onClick?: () => void;
    onPositionChange?: (centerX: number, centerY: number) => void;
    onPaddingChange?: (paddingX: number, paddingY: number) => void;
    playerWidth: number;
    playerHeight: number;
    videoWidth: number;
    videoHeight: number;
    stackOffsetPctY?: number;
}

const SubtitleOverlay = forwardRef<HTMLDivElement, SubtitleOverlayProps>(({
    subtitle,
    playerWidth,
    playerHeight,
    videoWidth,
    videoHeight,
    stackOffsetPctY = 0,
    onClick,
    onPositionChange,
    onPaddingChange
}, ref) => {
    const [overlayElement, setOverlayElement] = useState<HTMLDivElement | null>(null);
    const style: SubtitleStyle = subtitle?.style || defaultSubtitleStyle;

    const setRefs = (element: HTMLDivElement | null) => {
        setOverlayElement(element);
        if (typeof ref === 'function') {
            ref(element);
        } else if (ref) {
            ref.current = element;
        }
    };

    const fontMetrics = fontMetricsCache.get(style.fontFamily || '');
    const [, triggerRender] = useReducer((x) => x + 1, 0);

    const [position, setPosition] = useState<{x: number, y: number}>(() => {
        const x = subtitle?.style?.centerX ?? defaultSubtitleStyle.centerX!;
        const y = (subtitle?.style?.centerY ?? defaultSubtitleStyle.centerY!) - stackOffsetPctY;
        return { x, y };
    });
    const [isDragging, setIsDragging] = useState(false);
    const [contentDimensions, setContentDimensions] = useState({ width: 0, height: 0 });
    const dragStartRef = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number } | null>(null);
    const [isDraggingEdge, setIsDraggingEdge] = useState<'left' | 'right' | 'top' | 'bottom' | null>(null);
    const [padding, setPadding] = useState<{x: number, y: number}>(() => {
        const x = subtitle?.style?.paddingX ?? defaultSubtitleStyle.paddingX!;
        const y = subtitle?.style?.paddingY ?? defaultSubtitleStyle.paddingY!;
        return { x, y };
    });
    const edgeDragStartRef = useRef<{ mouseX: number; mouseY: number; paddingX: number; paddingY: number } | null>(null);
    const [isActive, setIsActive] = useState(false);

    const videoAspect = (videoWidth && videoHeight) ? videoWidth / videoHeight : 16/9;
    const playerAspect = (playerWidth && playerHeight) ? playerWidth / playerHeight : 16/9;

    let videoRectWidth = playerWidth;
    let videoRectHeight = playerHeight;
    let videoRectLeft = 0;
    let videoRectTop = 0;

    if (playerWidth > 0 && playerHeight > 0 && videoWidth > 0 && videoHeight > 0) {
        if (Math.abs(playerAspect - videoAspect) > 0.005) {
            if (playerAspect > videoAspect) {
                // Player is wider than video (Pillarbox)
                videoRectWidth = playerHeight * videoAspect;
                videoRectLeft = (playerWidth - videoRectWidth) / 2;
            } else {
                // Player is taller than video (Letterbox)
                videoRectHeight = playerWidth / videoAspect;
                videoRectTop = (playerHeight - videoRectHeight) / 2;
            }
        }
    }

    const pixelX = videoRectLeft + (videoRectWidth * (position.x / 100));
    const pixelY = videoRectTop + (videoRectHeight * (position.y / 100));

    useEffect(() => {
        if (!overlayElement) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.borderBoxSize?.[0]) {
                    setContentDimensions({
                        width: entry.borderBoxSize[0].inlineSize,
                        height: entry.borderBoxSize[0].blockSize
                    });
                } else if (entry.target instanceof HTMLElement) {
                    setContentDimensions({
                        width: entry.target.offsetWidth,
                        height: entry.target.offsetHeight
                    });
                }
            }
        });

        observer.observe(overlayElement);
        setContentDimensions({
            width: overlayElement.offsetWidth,
            height: overlayElement.offsetHeight
        });

        return () => observer.disconnect();
    }, [overlayElement]);

    useEffect(() => {
        if (subtitle) {
            setPosition({
                x: subtitle.style?.centerX ?? defaultSubtitleStyle.centerX!,
                y: (subtitle.style?.centerY ?? defaultSubtitleStyle.centerY!) - stackOffsetPctY
            });
            setPadding({
                x: subtitle.style?.paddingX ?? defaultSubtitleStyle.paddingX!,
                y: subtitle.style?.paddingY ?? defaultSubtitleStyle.paddingY!
            });
        }
    }, [subtitle?.style?.centerX, subtitle?.style?.centerY, subtitle?.style?.paddingX, subtitle?.style?.paddingY, stackOffsetPctY]);

    useEffect(() => {
        setIsActive(false);
    }, [subtitle?.id]);

    useEffect(() => {
        if (style.fontFamily && !fontMetricsCache.get(style.fontFamily)) {
            Promise.all([
                loadFont(style.fontFamily),
                getLineHeightRatio(style.fontFamily),
                getAssToCssScaleFactor(style.fontFamily)
            ])
                .then(([, lineHeightRatio, scaleFactor]) => {
                    const metrics = { lineHeightRatio, scaleFactor };
                    fontMetricsCache.set(style.fontFamily!, metrics);
                    triggerRender();
                })
                .catch(err => {
                    console.error('Failed to load font for preview:', err);
                    triggerRender();
                });
        }
    }, [style.fontFamily]);

    useEffect(() => {
        if (!isActive) return;

        const handleClickOutside = (e: PointerEvent) => {
            if (overlayElement && !overlayElement.contains(e.target as Node)) {
                setIsActive(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [isActive, overlayElement]);

    const getEventCoords = (e: React.MouseEvent | React.TouchEvent) =>
        'touches' in e
            ? { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY }
            : { clientX: e.clientX, clientY: e.clientY };

    const handleEdgeDragStart = (e: React.MouseEvent | React.TouchEvent, edge: 'left' | 'right' | 'top' | 'bottom') => {
        e.preventDefault();
        e.stopPropagation();
        const { clientX, clientY } = getEventCoords(e);
        setIsDraggingEdge(edge);
        edgeDragStartRef.current = {
            mouseX: clientX,
            mouseY: clientY,
            paddingX: padding.x,
            paddingY: padding.y
        };
    };

    const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const { clientX, clientY } = getEventCoords(e);
        setIsActive(true);
        onClick?.();
        setIsDragging(true);
        dragStartRef.current = {
            mouseX: clientX,
            mouseY: clientY,
            posX: position.x,
            posY: position.y
        };
    };

    useEffect(() => {
        if (!isDragging) return;

        const applyDelta = (clientX: number, clientY: number) => {
            if (!dragStartRef.current || !overlayElement) return;

            const deltaX = clientX - dragStartRef.current.mouseX;
            const deltaY = clientY - dragStartRef.current.mouseY;

            const deltaXPercent = (deltaX / videoRectWidth) * 100;
            const deltaYPercent = (deltaY / videoRectHeight) * 100;

            let newX = dragStartRef.current.posX + deltaXPercent;
            let newY = dragStartRef.current.posY + deltaYPercent;

            newX = Math.max(0, Math.min(100, newX));
            newY = Math.max(0, Math.min(100, newY));

            setPosition({ x: newX, y: newY });
        };

        const handleMouseMove = (e: MouseEvent) => applyDelta(e.clientX, e.clientY);

        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            applyDelta(e.touches[0].clientX, e.touches[0].clientY);
        };

        const finishDrag = () => {
            if (onPositionChange && dragStartRef.current) {
                const hasChanged = position.x !== dragStartRef.current.posX || position.y !== dragStartRef.current.posY;
                if (hasChanged) {
                    onPositionChange(position.x, position.y);
                }
            }
            setIsDragging(false);
            dragStartRef.current = null;
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', finishDrag);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', finishDrag);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', finishDrag);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', finishDrag);
        };
    }, [isDragging, videoRectWidth, videoRectHeight, position, onPositionChange, overlayElement]);

    useEffect(() => {
        if (!isDraggingEdge) return;

        const currentScaleFactor = videoHeight > 0 ? playerHeight / videoHeight : 1;

        const applyEdgeDelta = (clientX: number, clientY: number) => {
            if (!edgeDragStartRef.current) return;

            const deltaX = clientX - edgeDragStartRef.current.mouseX;
            const deltaY = clientY - edgeDragStartRef.current.mouseY;

            let newPaddingX = edgeDragStartRef.current.paddingX;
            let newPaddingY = edgeDragStartRef.current.paddingY;

            if (isDraggingEdge === 'left' || isDraggingEdge === 'right') {
                const sign = isDraggingEdge === 'right' ? 1 : -1;
                const paddingDelta = (sign * deltaX) / currentScaleFactor;
                newPaddingX = Math.max(1, Math.min(50, edgeDragStartRef.current.paddingX + paddingDelta));
            }

            if (isDraggingEdge === 'top' || isDraggingEdge === 'bottom') {
                const sign = isDraggingEdge === 'bottom' ? 1 : -1;
                const paddingDelta = (sign * deltaY) / currentScaleFactor;
                newPaddingY = Math.max(1, Math.min(50, edgeDragStartRef.current.paddingY + paddingDelta));
            }

            setPadding({ x: newPaddingX, y: newPaddingY });
        };

        const handleMouseMove = (e: MouseEvent) => applyEdgeDelta(e.clientX, e.clientY);

        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            applyEdgeDelta(e.touches[0].clientX, e.touches[0].clientY);
        };

        const finishEdgeDrag = () => {
            if (onPaddingChange && edgeDragStartRef.current) {
                const hasChanged = padding.x !== edgeDragStartRef.current.paddingX || padding.y !== edgeDragStartRef.current.paddingY;
                if (hasChanged) {
                    onPaddingChange(Math.round(padding.x), Math.round(padding.y));
                }
            }
            setIsDraggingEdge(null);
            edgeDragStartRef.current = null;
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', finishEdgeDrag);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', finishEdgeDrag);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', finishEdgeDrag);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', finishEdgeDrag);
        };
    }, [isDraggingEdge, videoHeight, playerHeight, padding, onPaddingChange]);

    if (!subtitle || !subtitle.text) {
        return null;
    }

    const scaleFactor = videoHeight > 0 ? playerHeight / videoHeight : 1;

    const referenceFontSize = style.fontSize ?? defaultSubtitleStyle.fontSize!;
    const referenceShadowDistance = style.shadowDistance;

    const paddingXScaled = padding.x * scaleFactor;
    const paddingYScaled = padding.y * scaleFactor;

    const color = style.color ?? defaultSubtitleStyle.color ?? 'rgba(255, 255, 255, 1)';
    const backgroundColor = style.backgroundColor ?? defaultSubtitleStyle.backgroundColor ?? 'rgba(0, 0, 0, 0.8)';
    const fontWeight = style.fontWeight ?? defaultSubtitleStyle.fontWeight ?? 'normal';
    const fontStyle = style.fontStyle ?? defaultSubtitleStyle.fontStyle ?? 'normal';
    const backgroundMode = style.backgroundMode ?? defaultSubtitleStyle.backgroundMode;
    const fontFamily = style.fontFamily ?? defaultSubtitleStyle.fontFamily!
    const lines = subtitle.text.split('\n');
    const maskBounds = backgroundMode === 'per-line' ? style.lineBounds : undefined;
    const lineBounds = maskBounds?.length === lines.length ? maskBounds : undefined;
    // Lines added or removed since masking no longer fit its rows, so one box keeps the original covered
    const boxBounds = maskBounds && !lineBounds ? lineBoundsBox(maskBounds) : undefined;

    const rawTargetFontSize = referenceFontSize * scaleFactor;
    const fontSize = rawTargetFontSize * (fontMetrics?.scaleFactor ?? 1);
    const lineHeightRatio = fontMetrics?.lineHeightRatio ?? 1.2;

    let textShadow = 'none';
    if (referenceShadowDistance && style.shadowColor) {
        const distance = referenceShadowDistance * scaleFactor;
        const color = style.shadowColor;
        textShadow = `${distance}px ${distance}px 0px ${color}`;
    }

    const subLeft = pixelX - contentDimensions.width / 2;
    const subTop = pixelY - contentDimensions.height / 2;

    const insetTop = Math.max(0, videoRectTop - subTop);
    const insetLeft = Math.max(0, videoRectLeft - subLeft);
    const insetRight = Math.max(0, (subLeft + contentDimensions.width) - (videoRectLeft + videoRectWidth));
    const insetBottom = Math.max(0, (subTop + contentDimensions.height) - (videoRectTop + videoRectHeight));

    const clipPath = `inset(${insetTop}px ${insetRight}px ${insetBottom}px ${insetLeft}px)`;

    const textStyle = {
        color,
        fontSize: `${fontSize}px`,
        fontFamily,
        fontWeight,
        fontStyle,
        textShadow,
        lineHeight: lineHeightRatio,
        fontKerning: 'none' as const,
        fontVariantLigatures: 'none' as const,
    };

    return (
        <div
            ref={setRefs}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'absolute',
                left: `${pixelX}px`,
                top: `${pixelY}px`,
                transform: 'translate(-50%, -50%) translateZ(0)',
                width: 'max-content',
                maxWidth: `${videoRectWidth}px`,
                cursor: isDragging ? 'grabbing' : isDraggingEdge ? 'grabbing' : 'grab',
                userSelect: 'none',
                zIndex: 10,
                textAlign: 'center',
                opacity: 0,
                willChange: 'opacity',
                outline: isActive ? '2px solid rgba(50, 231, 255, 1)' : 'none',
                outlineOffset: isActive ? '2px' : undefined,
            }}
            className="subtitle-overlay notranslate"
        >
            <div style={{ clipPath, width: '100%', height: '100%' }}>
                {backgroundMode === 'per-line' && !boxBounds ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        alignItems: 'center',
                        fontSize: 0,
                    }}>
                        {lines.map((line, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor,
                                    padding: `${paddingYScaled}px ${paddingXScaled}px`,
                                    display: 'inline-block',
                                    width: 'fit-content',
                                    ...(lineBounds && {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        minWidth: `${lineBounds[idx].width * scaleFactor}px`,
                                        minHeight: `${lineBounds[idx].height * scaleFactor}px`,
                                    }),
                                }}
                            >
                                <span
                                    dir="auto"
                                    style={{
                                        ...textStyle,
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    }}
                                >
                                    {line || '\u00A0'}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <span
                        dir="auto"
                        style={{
                            ...textStyle,
                            backgroundColor,
                            padding: `${paddingYScaled}px ${paddingXScaled}px`,
                            display: 'inline-block',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            ...(boxBounds && {
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                                minWidth: `${boxBounds.width * scaleFactor}px`,
                                minHeight: `${boxBounds.height * scaleFactor}px`,
                            }),
                        }}
                    >
                        {subtitle.text}
                    </span>
                )}
            </div>

            {isActive && (['left', 'right', 'top', 'bottom'] as const).map((edge) => {
                const isHorizontal = edge === 'left' || edge === 'right';
                return (
                    <div
                        key={edge}
                        onMouseDown={(e) => handleEdgeDragStart(e, edge)}
                        onTouchStart={(e) => handleEdgeDragStart(e, edge)}
                        style={{
                            position: 'absolute',
                            [edge]: -4,
                            [isHorizontal ? 'top' : 'left']: '50%',
                            transform: `translate(${edge === 'right' ? '50%' : '-50%'}, ${edge === 'bottom' ? '50%' : '-50%'})`,
                            cursor: isHorizontal ? 'ew-resize' : 'ns-resize',
                            width: 12,
                            height: 12,
                            backgroundColor: 'white',
                            border: '1px solid rgba(0, 0, 0, 0.3)',
                            borderRadius: '50%',
                            zIndex: 20,
                        }}
                    />
                );
            })}

            <div style={{ position: 'absolute', inset: 0, zIndex: -1 }} />
        </div>
    );
});

SubtitleOverlay.displayName = 'SubtitleOverlay';

export default SubtitleOverlay;
